<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="Super Admin" title="Stores" />
      </div>

      <div class="card">
        <div class="card-header">
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <h5 class="mb-0">
              All stores <small class="text-muted">({{ filtered.length }} / {{ stores.length }})</small>
            </h5>
            <div class="d-flex flex-wrap align-items-center mt-2 mt-md-0">
              <b-form-input
                v-model="searchQuery"
                size="sm"
                type="search"
                placeholder="Search host, title or owner..."
                style="max-width: 260px;"
                class="mr-2"
              ></b-form-input>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="loading || !filtered.length"
                @click="exportCsv"
              >
                <feather type="download" size="14" class="mr-1"></feather>Export CSV
              </button>
            </div>
          </div>
          <p class="text-muted small mb-0 mt-2">
            Every storefront across all tenants. Read-only here — manage a store’s settings from its own owner panel.
          </p>
          <p class="text-danger mb-0 mt-2" v-if="loadError">{{ loadError }}</p>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-3 mb-0">Loading stores...</p>
          </div>
          <div v-else-if="loadError" class="text-center py-5">
            <button class="btn btn-outline-primary btn-sm" @click="load">Retry</button>
          </div>
          <template v-else>
            <div class="table-responsive datatable-vue text-center">
              <b-table
                show-empty
                striped
                hover
                head-variant="light"
                bordered
                stacked="md"
                :items="filtered"
                :fields="fields"
                :per-page="perPage"
                :current-page="currentPage"
              >
                <template #empty><span>No store matches this search.</span></template>
                <template #cell(host)="field"><strong>{{ field.item.host }}</strong></template>
                <template #cell(title)="field">{{ field.item.title || "—" }}</template>
                <template #cell(currency)="field">{{ (field.item.currency || "—") | uppercase }}</template>
                <template #cell(status)="field">
                  <span v-if="field.item.status === null" class="badge badge-light text-muted">Unknown</span>
                  <span v-else :class="field.item.status ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ field.item.status ? "Enabled" : "Disabled" }}
                  </span>
                </template>
                <template #cell(ownerEmail)="field">
                  <span v-if="field.item.ownerEmail">{{ field.item.ownerEmail }}</span>
                  <span v-else class="text-muted">Orphan</span>
                </template>
              </b-table>
            </div>
            <b-col md="12" class="my-1 p-0 pagination-justify" v-if="filtered.length > perPage">
              <b-pagination
                v-model="currentPage"
                :total-rows="filtered.length"
                :per-page="perPage"
                class="mt-4"
              ></b-pagination>
            </b-col>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";
import { downloadCSV } from "@/utils/store-stats";

export default {
  name: "AdminStores",
  filters: {
    uppercase(value) {
      return String(value || "").toUpperCase();
    }
  },
  data() {
    return {
      fields: [
        { key: "host", label: "Host" },
        { key: "title", label: "Title" },
        { key: "currency", label: "Currency" },
        { key: "status", label: "Status" },
        { key: "ownerEmail", label: "Owner" }
      ],
      stores: [],
      searchQuery: "",
      currentPage: 1,
      perPage: 25,
      loading: false,
      loadError: ""
    };
  },
  computed: {
    filtered() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.stores;
      return this.stores.filter((s) => {
        return (
          String(s.host || "").toLowerCase().indexOf(q) !== -1 ||
          String(s.title || "").toLowerCase().indexOf(q) !== -1 ||
          String(s.ownerEmail || "").toLowerCase().indexOf(q) !== -1
        );
      });
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const res = await firebase.api.get("/admin/stores");
        this.stores = (res && res.stores) || [];
      } catch (e) {
        this.loadError = "Could not load stores: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    exportCsv() {
      const rows = this.filtered.map((s) => ({
        Host: s.host || "",
        Title: s.title || "",
        Currency: String(s.currency || "").toUpperCase(),
        Status: s.status === null ? "Unknown" : s.status ? "Enabled" : "Disabled",
        Owner: s.ownerEmail || ""
      }));
      downloadCSV(rows, "stores-" + new Date().toISOString().slice(0, 10) + ".csv");
    }
  }
};
</script>

<style lang="scss" scoped></style>
